<template lang='pug'>
  v-container(fluid v-if='testSuite')
    v-card.mb-2
      v-card-text
        v-text-field(label='Suite Name' @input='triggerSave' v-model='testSuite.name' type='text' required)
        v-textarea(outlined label='Description' @input='triggerSave' v-model='testSuite.description' type='text' auto-grow rows='3')
        v-text-field(label='Base URL' placeholder='Base URL prefix (e.g. https://example.com)' @input='triggerSave' v-model='testSuite.baseUrl' type='text' required)
        tags(v-model='testSuite.tags' @input='triggerSave')
        v-combobox(@input='triggerSave'
          v-model='testSuite.environments'
          :items='allEnvironments'
          label='Browsers'
          multiple
          chips
          v-if='user.superAdmin || isAssumedAccount'
          item-text='text'
          item-value='browser')
          template(v-slot:selection='{attrs, item, index, selected, disabled, parent}')
            v-chip.v-chip--select-multi(close v-bind='attrs' :input-value='selected' :disabled='disabled' :key='index' @click:close='parent.selectItem(item)')
              img.image(:src='getBrowser(item.browser)')
              | &nbsp;{{item.browser | browser}}
    v-card.mb-2
      v-card-title(@click.stop.prevent='hideInputs = !hideInputs')
        .text-h5.font-weight-light Inputs
        v-row(justify='end' align='start')
          v-tooltip(bottom v-if='!testSuite.pureJson')
            template(v-slot:activator='{on}')
              v-btn(v-on='on' icon @click.stop='switchToJson()')
                v-icon code
            span Switch to JSON View
          v-tooltip(bottom v-if='testSuite.pureJson')
            template(v-slot:activator='{on}')
              v-btn(v-on='on' icon @click.stop='switchToList()')
                v-icon list
            span Switch to List View
          v-tooltip(bottom)
            template(v-slot:activator='{on}')
              v-btn(v-on='on' icon @click.stop='addInput()')
                v-icon add
            span Add
      v-card-text(v-if='!hideInputs && !testSuite.pureJson').pt-2
        v-row(align='center' justify='center' v-if='!testSuite.data || !testSuite.data.length')
          .text-h5.grey--text No Inputs Defined
        v-row(v-for='(input, index) in testSuite.data' :key='index' no-gutters).pb-0
          v-col(cols='4')
            v-text-field(solo label='Key' v-model='input.key' @input='triggerSave')
          v-col(cols='8')
            v-text-field(solo append-outer-icon='delete' label='Value' @input='triggerSave' @click:append-outer='removeInput(index)' v-model='input.value')
      bionic-metrics-monaco-editor.inline-editor(
        v-if='!hideInputs && testSuite.pureJson && codeReady'
        ref='inlineEditor'
        type='input'
        :guid='testSuite.guid'
        @input='triggerSave'
        v-model='testSuite.json'
        mode='json')
    v-card.mb-2
      v-card-title(@click.stop.prevent='hideTests = !hideTests')
        h3.text-h5.font-weight-light Tests
      v-card-text(v-if='!hideTests')
        // no idea why item-text fixes this...
        v-autocomplete(label='Search to Add Tests'
          @input='addTest'
          v-model='test'
          :loading='searchLoading'
          :items='availableTests'
          hide-selected
          hide-no-data
          item-text='name'
          :search-input.sync='search'
          no-data-text='No Tests Found'
          return-object)
          template(v-slot:item='{item}')
            v-list-item-content
              v-list-item-title(v-text='item.name')
              v-list-item-subtitle(v-text='item.description')
      sortable-list(v-if='!hideTests' ref='sortableList' :value='testSuite.tests' @input='updateTests')
        template(v-slot:row='{item, index}')
          div.ml-2
            .text-subtitle-1 {{ item.name }}
            .text-caption.grey--text(v-if='item.disabled') Disabled
          //v-row(no-gutters align='center' justify='end')
          v-spacer
          div
            v-tooltip(bottom)
              template(v-slot:activator='{on}')
                v-btn(v-on='on' text icon @click='disableTest(index)')
                  v-icon not_interested
              span(v-if='!item.disabled') Disable
              span(v-if='item.disabled') Enable
            v-tooltip(bottom)
              template(v-slot:activator='{on}')
                v-btn(v-on='on' text icon @click='duplicateTest(index)')
                  v-icon file_copy
              span Copy
            v-tooltip(bottom)
              template(v-slot:activator='{on}')
                v-btn(v-on='on' text icon @click='to(`/test/${item.guid}`)')
                  v-icon edit
              span Edit
            v-tooltip(bottom)
              template(v-slot:activator='{on}')
                v-btn(v-on='on' text icon @click='removeTest(index)')
                  v-icon delete
              span Remove
    v-card.mb-2
      v-card-title(@click.stop.prevent='hideSetup = !hideSetup')
        v-col.pa-0
          h3.text-h5.font-weight-light Setup Suites
          .text-caption.font-weight-light(v-if='!hideSetup')
            | Run before the current suite starts
      v-card-text(v-if='!hideSetup')
        // no idea why item-text fixes this...
        v-autocomplete(label='Search to Add'
          @input='addSetup'
          v-model='setupTestSuite'
          :loading='searchSetupSuitesLoading'
          :items='availableTestSuites'
          hide-selected hide-no-data item-text='name'
          :search-input.sync='searchSetupSuites'
          no-data-text='No Suites Found'
          return-object)
          template(v-slot:item='{item}')
            v-list-item-content
              v-list-item-title(v-text='item.name')
              v-list-item-subtitle(v-text='item.description')
      sortable-list(v-if='!hideSetup' ref='sortableSetupList' :value='testSuite.setup' @input='updateSetup')
        template(v-slot:row='{item, index}')
          div {{ item.name }}
          v-spacer
          v-btn(text icon :to='`/testsuite/${item.guid}`' target='_blank')
            v-icon edit
          v-btn(text icon @click='removeSetup(index)')
            v-icon delete
    v-card.mb-2
      v-card-title(@click.stop.prevent='hideFinally = !hideFinally')
        v-col.pa-0
          h3.text-h5.font-weight-light Cleanup Suites
          .text-caption.font-weight-light(v-if='!hideFinally')
            | Run after the current suite completes regardless of success or failure
      v-card-text(v-if='!hideFinally')
        // no idea why item-text fixes this...
        v-autocomplete(label='Search to Add'
          @input='addFinally'
          v-model='finallyTestSuite'
          :loading='searchSuitesLoading'
          :items='availableTestSuites'
          hide-selected hide-no-data item-text='name'
          :search-input.sync='searchSuites'
          no-data-text='No Suites Found'
          return-object)
          template(v-slot:item='{item}')
            v-list-item-content
              v-list-item-title(v-text='item.name')
              v-list-item-subtitle(v-text='item.description')
      sortable-list(v-if='!hideFinally' ref='sortableFinallyList' :value='testSuite.finally' @input='updateFinally')
        template(v-slot:row='{item, index}')
          div {{ item.name }}
          v-spacer
          v-btn(text icon :to='`/testsuite/${item.guid}`' target='_blank')
            v-icon edit
          v-btn(text icon @click='removeFinally(index)')
            v-icon delete
    v-card.mb-2
      v-card-title(@click.stop.prevent='hideExtensions = !hideExtensions')
        v-col.pa-0
          h3.text-h5.font-weight-light Browser Extensions
          .text-caption.font-weight-light(v-if='!hideExtensions')
            | CRX Extensions to be injected into the browser under test
        v-row(justify='end' align='start')
          v-tooltip(bottom)
            template(v-slot:activator='{on}')
              v-btn(v-on='on' @click.stop='$refs.inputUpload.click()' icon :loading='extensionUploading').mr-2
                v-icon cloud_upload
            span Upload Extension
            input(v-show='false' ref='inputUpload' type='file' @change='uploadExtension')
      v-card-text(v-if='!hideExtensions')
        v-row(align='center' justify='center' v-if='!testSuite.extensions || !testSuite.extensions.length')
          .text-h5.grey--text No Extensions Added
      div(v-for='(extension, index) in testSuite.extensions' :key='index' v-if='!hideExtensions')
        v-divider
        v-row(align='center').mr-4.ml-4.pt-2.pb-2
          a(:href='extension.url' target='_blank') {{ extension.name }}
          v-spacer
          v-icon(@click='removeExtension(index)') delete
      v-divider
    v-card.mb-2
      v-card-title(@click.stop.prevent='hideAdvanced = !hideAdvanced')
        h3.text-h5.font-weight-light Advanced
      v-card-text(v-if='!hideAdvanced')
        v-checkbox(v-if='user.superAdmin'
          @change='triggerSave'
          v-model='testSuite.useProxy'
          label="Use Proxy"
          persistent-hint
          hint='Use the Bionic Metrics proxy to have all requests come from a known IP address.')
        v-checkbox(@change='triggerSave'
          v-model='testSuite.noBrowser'
          label='No Browser'
          persistent-hint
          hint='Turn off browser for all tests (for pure API testing only)')
        v-checkbox(:disabled='isParallel'
          @change='triggerSave'
          v-model='testSuite.perTestVideo'
          label='Per Test Video'
          persistent-hint
          hint='Record video per test instead of for the full suite.')
        v-checkbox(:disabled='testSuite.parallel'
          @change='triggerSave'
          v-model='testSuite.resetForEachTest'
          label='Reset Browser for Each Test'
          persistent-hint
          hint='Reset the browser to a clean state for each test.')
        v-checkbox(:disabled='isParallel'
          @change='triggerSave'
          v-model='testSuite.continueOnFail'
          label='Continue On Fail'
          persistent-hint
          hint='Continue to run even if a failure occurs as opposed to stopping on the first failed test.')
        v-checkbox(@change='triggerSave'
          v-if='hasMachineParallel'
          v-model='testSuite.machineParallel'
          label='Run On Multiple Machines'
          persistent-hint
          hint='Run tests in parallel on multiple machines')
        v-slider.mt-4(@change='triggerSave'
          v-model='testSuite.machineConcurrency'
          v-if='hasMachineParallel && testSuite.machineParallel'
          min='1'
          :max='machineConcurrencyMax'
          thumb-label
          label='Number of Machines'
          persistent-hint
          hint='Number of machines to run in parallel')
        v-checkbox(@change='triggerSave'
          v-if='hasThreads'
          v-model='testSuite.parallel'
          label='Run In Parallel Threads'
          persistent-hint
          hint='Run tests in parallel threads. The maximum number of threads possible depends on the load of the running tests.\
          Some experimentation may be required to determine the maximum number of threads possible.')
        v-slider.mt-4(@change='triggerSave'
          v-model='testSuite.concurrency'
          v-if='hasThreads && testSuite.parallel'
          min='1'
          :max='concurrencyMax'
          thumb-label
          label='Concurrency'
          persistent-hint
          hint='Number of threads to run in parallel')
        v-checkbox(@change='triggerSave'
          v-model='testSuite.heavy'
          v-if='hasHeavyAgents && hasThreads && testSuite.parallel && testSuite.concurrency'
          label='Run On Heavy Agent'
          persistent-hint
          hint='Run this suite on a heavy agent (greater compute power). \
          There may be added warm up time for the heavy agent to start up.')
        v-text-field(v-if='user.superAdmin'
          label='Watchdog Override'
          persistent-hint
          type='number'
          v-model='testSuite.watchdogTimeoutOverride'
          @input='triggerSave'
          hint='Enter value in milliseconds (300000) for 300000ms = 5 minutes' ).mt-4
        timezone(v-if='hasTimezone'
          v-model='testSuite.timezone'
          @input='triggerSave'
          hint='Emulate a timezone in the browser').mt-4
        bionic-metrics-user-agent.mt-4(v-model='testSuite.userAgent' @input='triggerSave')
    div.space
</template>

<script>
import BionicMetricsUserAgent from '@/components/UserAgent';
import _ from 'lodash';
import {mapGetters, mapActions, mapMutations} from 'vuex';
import Notifications from '../../services/notification.service';
import Upload from '../../services/upload.service';
import SortableList from '../../components/SortableList';
import BionicMetricsRunningIndicator from '../../components/RunningIndicator';
import BionicMetricsTestStatusIndicator from '../../components/TestStatusIndicator';
import Test from '../../api/test';
import TestSuite from '../../api/testSuite';
import EnvironmentMixin from '../../mixins/environment.mixin';
import SaveMixin from '../../mixins/save.mixin';
import BionicMetricsMonacoEditor from '../../components/MonacoEditor';
import Tags from '../../components/Tags';
import Timezone from '../../components/Timezone';

export default {
  name: 'TestSuite',
  components: {
    BionicMetricsUserAgent,
    Timezone,
    Tags,
    BionicMetricsMonacoEditor,
    BionicMetricsTestStatusIndicator,
    BionicMetricsRunningIndicator,
    SortableList
  },
  props: {
    guid: {
      required: true,
      type: String
    },
    redirect: {
      type: Boolean,
      default: true
    }
  },
  async mounted() {
    await this.load();
  },
  async beforeDestroy() {
    await this.saveOutstanding();
  },
  computed: {
    isParallel() {
      return _.get(this.testSuite, 'parallel') || _.get(this.testSuite, 'machineParallel');
    },
    hasThreads() {
      return _.get(this.account, 'entitlements.hasThreads') || this.hasHeavyAgents || _.get(this.user, 'superAdmin');
    },
    hasHeavyAgents() {
      return _.get(this.account, 'entitlements.hasHeavyAgents') || _.get(this.user, 'superAdmin');
    },
    hasMachineParallel() {
      return _.get(this.account, 'entitlements.hasMachineParallel') || _.get(this.user, 'superAdmin');
    },
    hasTimezone() {
      return _.get(this.account, 'entitlements.hasTimezone') || _.get(this.user, 'superAdmin');
    },
    codeReady() {
      return !this.loading && !_.isUndefined(this.testSuite.json);
    },
    concurrencyMax() {
      return this.testSuite.noBrowser || _.get(this.user, 'superAdmin') ? 10 : 5;
    },
    machineConcurrencyMax() {
      return _.get(this.user, 'superAdmin') ? 250 : 10;
    },
    saveObject() {
      return this.testSuite;
    },
    ...mapGetters({
      isAssumedAccount: 'isAssumedAccount',
      user: 'getUser',
      account: 'getAccount'
    })
  },
  methods: {
    abort() {
      return this.$router.push('/');
    },
    async load() {
      try {
        this.loading = true;
        this.testSuite = await TestSuite.get(this.guid);
        if (!this.testSuite.tags) this.testSuite.tags = [];
        this.getAvailableTests();
        this.getAvailableTestSuites();
      } catch (error) {
        console.error(error);
        return this.abort();
      } finally {
        this.loading = false;
      }
    },
    async enforceFlags() {
      if (this.isParallel) {
        this.testSuite.continueOnFail = true;
        if (this.testSuite.parallel) {
          // this.testSuite.perTestVideo = false;
          this.testSuite.resetForEachTest = true;
        }
        if (this.testSuite.machineParallel) {
          this.testSuite.perTestVideo = true;
        }
      }
    },
    async save(suite) {
      try {
        await TestSuite.update(suite.guid, suite);
        Notifications.toast('Save Successful');
      } catch (error) {
        console.error(`Error saving suite: ${error.message}`);
        Notifications.errorToast('There was a problem saving the Suite', {duration: 30000, retry: this.save});
      }
    },
    updateBreadcrumb() {
      this.setBreadcrumb([{text: 'Suites', to: '/testsuites'}, {text: this.testSuite.name}]);
    },
    remove(item) {
      this.chips.splice(this.chips.indexOf(item), 1);
      this.chips = [...this.chips];
    },
    async removeTest(index) {
      this.testSuite.tests.splice(index, 1);
      this.triggerSave();
    },
    async addTest(test) {
      console.log(test.name);
      const testInstance = {
        name: test.name,
        guid: test.guid,
        test: test._id
      };
      if (!this.testSuite.tests) this.testSuite.tests = [];
      this.testSuite.tests.push(testInstance);
      this.triggerSave();
      this.$nextTick(() => {
        this.search = false;
        this.test = null;
        this.$refs.sortableList.scrollToBottom();
      });
    },
    async updateTests(tests) {
      if (!this.testSuite.tests) this.testSuite.tests = [];
      this.testSuite.tests.splice(0, tests.length, ...tests);
      this.triggerSave();
    },
    async duplicateTest(index) {
      const copy = {...this.testSuite.tests[index]};
      this.testSuite.tests.splice(index, 0, copy);
      this.triggerSave();
    },
    async disableTest(index) {
      this.testSuite.tests[index].disabled = !this.testSuite.tests[index].disabled;
      this.triggerSave();
    },
    async addSetup(testSuite) {
      console.log(testSuite.name);
      const setupSuite = {
        name: testSuite.name,
        guid: testSuite.guid,
        testSuite: testSuite._id
      };
      if (!this.testSuite.setup) this.testSuite.setup = [];
      this.testSuite.setup.push(setupSuite);
      this.triggerSave();
      this.$nextTick(() => {
        this.searchSetupSuites = false;
        this.setupTestSuite = null;
        this.$refs.sortableSetupList.scrollToBottom();
      });
    },
    async removeSetup(index) {
      this.testSuite.setup.splice(index, 1);
      this.triggerSave();
    },
    async updateSetup(suites) {
      if (!this.testSuite.setup) this.testSuite.setup = [];
      this.testSuite.setup.splice(0, suites.length, ...suites);
      this.triggerSave();
    },
    async addFinally(testSuite) {
      console.log(testSuite.name);
      const finallySuite = {
        name: testSuite.name,
        guid: testSuite.guid,
        testSuite: testSuite._id
      };
      if (!this.testSuite.finally) this.testSuite.finally = [];
      this.testSuite.finally.push(finallySuite);
      this.triggerSave();
      this.$nextTick(() => {
        this.searchSuites = false;
        this.finallyTestSuite = null;
        this.$refs.sortableFinallyList.scrollToBottom();
      });
    },
    async removeFinally(index) {
      this.testSuite.finally.splice(index, 1);
      this.triggerSave();
    },
    async updateFinally(suites) {
      if (!this.testSuite.finally) this.testSuite.finally = [];
      this.testSuite.finally.splice(0, suites.length, ...suites);
      this.triggerSave();
    },
    async getAvailableTestSuites(search) {
      this.searchSuitesLoading = true;
      const projection = {
        name: true,
        description: true,
        guid: true
      };
      const limit = 10;
      const {list} = await TestSuite.list({limit, projection, search});
      this.availableTestSuites = list;
      this.searchSuitesLoading = false;
    },
    async getAvailableTests(search) {
      this.searchLoading = true;
      const projection = {
        name: true,
        description: true,
        guid: true
      };
      const limit = 10;
      const {list} = await Test.list({limit, projection, search});
      this.availableTests = list;
      this.searchLoading = false;
    },
    async switchToJson() {
      if (!(await Notifications.confirm('Switch', 'Are you sure you want to switch to raw JSON?'))) return;
      this.testSuite.json = JSON.stringify(_.mapValues(_.keyBy(this.testSuite.data, 'key'), 'value'), false, 4); //https://youtu.be/70o_F6Yo7ck?t=28
      this.testSuite.pureJson = true;
      this.triggerSave();
    },
    async switchToList() {
      if (!(await Notifications.confirm('Switch', 'Are you sure you want to switch to a list view. This can result in data reformatting?'))) return;
      const json = JSON.parse(this.testSuite.json);
      this.testSuite.data = Object.keys(json).map((key) => ({key, value: json[key]}));
      this.testSuite.pureJson = false;
      this.triggerSave();
    },
    async uploadExtension(evt) {
      this.extensionUploading = true;
      try {
        const files = _.get(evt, 'target.files');
        if (!files) {
          console.error('No files provided.');
          return;
        }
        for (const file of files) {
          const extension = await Upload.uploadFile(this.testSuite.guid, file);
          if (!this.testSuite.extensions) this.testSuite.extensions = [];
          this.testSuite.extensions.push(extension);
        }
      } finally {
        this.extensionUploading = false;
      }
    },
    removeExtension(index) {
      this.testSuite.extensions.splice(index, 1);
      this.triggerSave();
    },
    addInput() {
      this.hideInputs = false;
      if (!this.testSuite.data) this.testSuite.data = [];
      this.testSuite.data.push({key: '', value: ''});
      this.triggerSave();
    },
    async removeInput(index) {
      if (!(await Notifications.confirm('Remove', 'Are you sure you want to this input from this suite?'))) return;
      this.testSuite.data.splice(index, 1);
      this.triggerSave();
    },
    to(route) {
      this.$router.push(route);
    },
    clearDirty() {
      console.log('Save mixin saved and we are no longer dirty');
    },
    ...mapActions({
      switchTestSuiteToJson: 'switchTestSuiteToJson',
      switchTestSuiteToList: 'switchTestSuiteToList'
    }),
    ...mapMutations({
      setBreadcrumb: 'setBreadcrumb'
    })
  },
  mixins: [EnvironmentMixin, SaveMixin],
  watch: {
    'testSuite.name': {
      handler: function () { // eslint-disable-line
        this.updateBreadcrumb();
      },
      deep: true
    },
    isParallel() {
      this.enforceFlags();
    },
    // eslint-disable-next-line
    search: _.debounce(function (val) {
      this.getAvailableTests(val);
    }, 250),
    // eslint-disable-next-line
    searchSuites: _.debounce(function (val) {
      this.getAvailableTestSuites(val);
    }, 250),
    // eslint-disable-next-line
    searchSetupSuites: _.debounce(function (val) {
      this.getAvailableTestSuites(val);
    }, 250)
  },
  data() {
    return {
      loading: false,
      searchLoading: false,
      search: null,
      searchSuitesLoading: false,
      searchSuites: null,
      searchSetupSuitesLoading: false,
      searchSetupSuites: null,
      availableTests: [],
      availableTestSuites: [],
      select: [],
      test: null,
      intervalOptions: [
        {text: 'Disabled', value: 'disabled'},
        {text: 'Every Hour', value: '1hour'},
        {text: 'Every 2 Hours', value: '2hours'},
        {text: 'Every 4 Hours', value: '4hours'},
        {text: 'Every 8 Hours', value: '8hours'},
        {text: 'Every 12 Hours', value: '12hours'},
        {text: 'Every Day', value: '1day'},
        {text: 'Every Other Day', value: '2days'},
        {text: 'Every Week', value: '1week'}
      ],
      finallyTestSuite: null,
      setupTestSuite: null,
      hideInputs: false,
      hideTests: false,
      hideSetup: false,
      hideFinally: false,
      hideExtensions: false,
      hideAdvanced: false,
      extensionUploading: false,
      testSuite: {
        environments: [],
        data: [],
        extensions: [],
        tests: []
      }
    };
  }
};

</script>

<style scoped>
  .space {
    min-height: 75vh;
  }

  .inline-editor {
    width: 100%;
    min-height: 40vh;
  }

  .action-item {
    margin: 5px;
  }

  .code {
    font-family: monospace;
    background: #f4f4f4;
    border: 1px solid #ddd;
    border-left: 3px solid #455A64;
    color: #666;
    page-break-inside: avoid;
    font-size: 15px;
    line-height: 1.6;
    margin-bottom: 1.6em;
    max-width: 100%;
    overflow: auto;
    padding: 1em 1.5em;
    display: block;
    word-wrap: break-word;
  }

  .image {
    margin: 2px;
    height: 20px;
    width: 20px;
  }
</style>
